import Vue from 'vue'
import VueRouter from 'vue-router'
import { auth } from '../services/firebase'
import store from '../store'

Vue.use(VueRouter)

const routes = [
  // Auth routes redirect
  {
    path: '/auth',
    redirect: '/auth/welcome'
  },
  // Public auth routes
  {
    path: '/auth/welcome',
    name: 'AuthWelcome',
    component: () => import('../views/AuthWelcome.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/auth/signup',
    name: 'AuthSignup',
    component: () => import('../views/AuthSignup.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/auth/login',
    name: 'AuthLogin',
    component: () => import('../views/AuthLogin.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/auth/verify',
    name: 'AuthVerify',
    component: () => import('../views/AuthVerify.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/auth/profile',
    name: 'AuthProfile',
    component: () => import('../views/AuthProfile.vue'),
    meta: { requiresAuth: true, isProfileSetup: true }
  },
  // Protected routes
  {
    path: '/welcome',
    name: 'Welcome',
    component: () => import('../views/WelcomeView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/properties',
    name: 'Properties',
    component: () => import('../views/Properties.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/tasks',
    name: 'Tasks',
    component: () => import('../views/Tasks.vue'),
    meta: { requiresAuth: true }
  },
  // Collateral routes
  {
    path: '/collateral',
    name: 'CollateralList',
    component: () => import('../views/CollateralList.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/collateral/upload',
    name: 'CollateralUpload',
    component: () => import('../views/CollateralUpload.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/collateral/:id',
    name: 'CollateralDetail',
    component: () => import('../views/CollateralDetail.vue'),
    meta: { requiresAuth: false }
  },
  // Follow-up routes
  {
    path: '/followups',
    name: 'FollowUpList',
    component: () => import('../views/FollowUpList.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/followups/new',
    name: 'FollowUpComposer',
    component: () => import('../views/FollowUpComposer.vue'),
    meta: { requiresAuth: true }
  },
  // Voice result route
  {
    path: '/voice/result',
    name: 'VoiceResult',
    component: () => import('../views/VoiceResultView.vue'),
    meta: { requiresAuth: true }
  },
  // CRM routes
  {
    path: '/crm',
    name: 'CRMDashboard',
    component: () => import('../views/CRMDashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/crm/new',
    name: 'ClientCreate',
    component: () => import('../views/ClientCreate.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/crm/clients/:clientId',
    name: 'ClientDetail',
    component: () => import('../views/ClientDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/crm/clients/:clientId/edit',
    name: 'EditClientDetail',
    component: () => import('../views/EditClientDetail.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/crm/clients/:clientId/enrichment',
    name: 'EnrichmentDetail',
    component: () => import('../views/EnrichmentDetail.vue'),
    meta: { requiresAuth: true }
  },
  // Legacy routes (keep Dashboard for backward compatibility)
  {
    path: '/',
    redirect: '/welcome'
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  // Legacy login route redirect
  {
    path: '/login',
    redirect: '/auth/login'
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// vue-router 3.1+ returns a Promise from programmatic navigation that rejects on
// a NavigationFailure. Cancelled (a newer navigation interrupted this one),
// Duplicated (already at the target), and Redirected failures are benign — the
// user still ends up where they should — but uncaught they surface as unhandled
// promise rejections and get reported as errors (e.g. to Sentry). Swallow only
// those benign failures so real navigation errors still propagate.
const { isNavigationFailure, NavigationFailureType } = VueRouter
const BENIGN_FAILURES = [
  NavigationFailureType.cancelled,
  NavigationFailureType.duplicated,
  NavigationFailureType.redirected
]
const isBenignFailure = err =>
  BENIGN_FAILURES.some(type => isNavigationFailure(err, type))

;['push', 'replace'].forEach(method => {
  const original = router[method].bind(router)
  router[method] = function (location, onResolve, onReject) {
    if (onResolve || onReject) {
      return original(location, onResolve, onReject)
    }
    return original(location).catch(err => {
      if (isBenignFailure(err)) {
        return err
      }
      return Promise.reject(err)
    })
  }
})

// Navigation guards
router.beforeEach((to, from, next) => {
  const VALID_PROVIDERS = ['deepgram', 'soniox']
  if (VALID_PROVIDERS.includes(to.query.provider)) {
    store.dispatch('setProvider', to.query.provider)
  }

  const currentUser = auth.currentUser
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest)
  const isProfileSetup = to.matched.some(record => record.meta.isProfileSetup)

  // Set page title: "Amatyaa" for authenticated routes, "Document" for public/guest routes
  if (requiresAuth && currentUser) {
    document.title = 'Amatyaa'
  } else {
    document.title = 'Document'
  }

  // Special handling for /auth/profile
  if (to.path === '/auth/profile') {
    if (!currentUser) {
      // Not authenticated, redirect to auth welcome
      next('/auth/welcome')
      return
    }

    // Check if user should access profile setup
    const { pendingProfile } = store.state.signupContext
    if (!pendingProfile) {
      // No pending profile setup, redirect to welcome
      next('/welcome')
      return
    }

    // Allow access to profile setup
    next()
    return
  }

  // Rule 1: If authenticated and trying to access /auth/* routes (except /auth/profile), redirect to /welcome
  if (currentUser && to.path.startsWith('/auth') && !isProfileSetup) {
    next('/welcome')
    return
  }

  // Rule 2: If not authenticated and trying to access /welcome, redirect to /auth/welcome
  if (!currentUser && to.path === '/welcome') {
    next('/auth/welcome')
    return
  }

  // General auth guards
  if (requiresAuth && !currentUser) {
    // Not authenticated, redirect to auth welcome
    next('/auth/welcome')
  } else if (requiresGuest && currentUser) {
    // Already authenticated, redirect to welcome
    next('/welcome')
  } else {
    next()
  }
})


export default router
